import { useRef, useState } from "react";
import { Volume2, VolumeX, PlayCircle, PauseCircle, Sparkles } from "lucide-react";
import { IMAGES, ONLINE_SESSION_POINTS } from "@/lib/content";
import { Reveal, SectionLabel } from "@/components/landing/Reveal";
import { EligibilityButton } from "@/components/landing/EligibilityButton";

export const OnlineClassInAction = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden bg-ink2 px-4 py-16 diag-grid sm:px-8 lg:py-24"
      data-testid="online-session-section"
    >
      <div className="pointer-events-none absolute -left-32 top-24 h-96 w-96 rounded-full bg-em1/15 blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-em2/10 blur-[120px]" />

      <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-12 lg:gap-14">
        {/* Video phone-frame */}
        <Reveal className="lg:col-span-5">
          <div className="relative mx-auto w-full max-w-[320px]">
            <div className="pointer-events-none absolute inset-0 rounded-[2.4rem] bg-gradient-to-br from-em1/40 via-transparent to-gold/25 blur-2xl" />
            <div className="relative rounded-[2.4rem] border border-surfaceline bg-black p-2 shadow-2xl glow-emerald">
              <div className="relative overflow-hidden rounded-[1.9rem] aspect-[9/16]">
                <video
                  ref={videoRef}
                  src={IMAGES.reel}
                  data-testid="online-reel-video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/60 to-transparent" />

                <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-em2/40 bg-black/60 px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-wider text-em2 backdrop-blur">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-em2 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-em2" />
                  </span>
                  Live Session
                </span>

                <div className="absolute inset-x-0 bottom-3 flex items-center justify-between px-3">
                  <button
                    onClick={togglePlay}
                    aria-label={playing ? "Pause" : "Play"}
                    data-testid="online-reel-play-toggle"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-black/55 text-white backdrop-blur transition-colors hover:bg-black/75"
                  >
                    {playing ? <PauseCircle size={22} /> : <PlayCircle size={22} />}
                  </button>
                  <button
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute" : "Mute"}
                    data-testid="online-reel-mute-toggle"
                    className="flex h-10 items-center gap-1.5 rounded-full border border-white/30 bg-black/55 px-3 text-xs font-semibold text-white backdrop-blur transition-colors hover:bg-black/75"
                  >
                    {muted ? (
                      <>
                        <VolumeX size={16} /> Tap to unmute
                      </>
                    ) : (
                      <>
                        <Volume2 size={16} /> Sound on
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="absolute left-1/2 top-0 h-1.5 w-16 -translate-x-1/2 translate-y-1 rounded-full bg-surface" />
            </div>
            <p className="mt-4 text-center text-xs font-semibold uppercase tracking-[0.22em] text-mutedgreen">
              See a Real Live Session
            </p>
          </div>
        </Reveal>

        {/* Copy */}
        <div className="lg:col-span-7">
          <Reveal>
            <SectionLabel>Inside The Live Session</SectionLabel>
            <h2 className="mt-4 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-offwhite sm:text-6xl">
              See Exactly What Your{" "}
              <span className="text-em2">90 Days</span> Look Like
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-mutedgreen sm:text-lg">
              Mentor on screen, live charts, real trades reviewed. Every session
              moves you closer to a written plan and 10 planned live trades. No
              fluff, no fake gurus.
            </p>

            <ul className="mt-6 space-y-2.5">
              {ONLINE_SESSION_POINTS.map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-offwhite">
                  <Sparkles size={16} strokeWidth={1.6} className="mt-0.5 shrink-0 text-gold" />
                  {t}
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <EligibilityButton size="lg" label="Reserve My Free Trial Seat" testId="online-session-cta" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
